import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { Component, computed, HostListener, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { EMPTY, Observable, of, switchMap, take, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { ProtestantCanonBooks } from '../../../models/books.bible.model';
import { MembershipStatus } from '../../../models/membership.status';
import { QuestionType, QuizQuestion } from '../../../models/question.quiz.model';
import { Quiz, QuizStatus } from '../../../models/quiz.model';
import { MomentPipe } from '../../../pipes/moment.pipe';
import { QuizStatusPipe } from '../../../pipes/quizStatus.pipe';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AppState } from '../../../state/appstate';
import { AlertType } from '../../components/dialogs/confirm.dialog.model';
import { BtnMoreComponent } from '../../components/buttons/btn-more/btn-more.component';
import { ChipColor, ChipComponent } from '../../components/inputs/chip/chip.component';
import { QuizDetailComponent } from './quiz-detail.component';

@Component({
  selector: 'app-quiz-info',
  standalone: true,
  imports: [
    ChipComponent,
    AsyncPipe,
    TitleCasePipe,
    MomentPipe,
    BtnMoreComponent,
    QuizStatusPipe,
    RouterLink,
  ],
  templateUrl: './quiz-info.component.html',
  styleUrl: './quiz-detail.component.css', // reuse parent styles
})
export class QuizInfoComponent implements OnInit {
  parent = inject(QuizDetailComponent);
  private quizService = inject(QuizService);
  private logger = inject(NGXLogger);
  private utilsService = inject(UtilsService);
  private toastrService = inject(ToastrService);

  attempts$: Observable<any[]> = of([]);
  loadingAttempts = signal(false);
  hasExistingAttempt = signal(false);
  showStatusDropdown = signal(false);

  participantAnswers = signal<any[]>([]);
  participantScore = signal<any>(null);
  isLoadingParticipantData = signal(false);

  selectedQuestions = signal<QuizQuestion[]>([]);

  chipColor = ChipColor;
  quizStatus = QuizStatus;
  membershipStatus = MembershipStatus;
  protestantBooks = ProtestantCanonBooks;
  questionTypes = Object.values(QuestionType);

  totalScore = computed(() => {
    return this.selectedQuestions().reduce((acc, q) => acc + (q.score || 0), 0);
  });

  ngOnInit() {
    this.loadAttempts();
    this.parent.myRoles$.pipe(take(1)).subscribe((roles) => {
      // Load questions for creator or management
      if (
        (roles.faculty && this.parent.quiz?.createdById === this.parent.user?.uid) ||
        roles.management
      ) {
        this.loadQuestions();
      }
    });
  }

  loadQuestions() {
    const groupId = this.parent.groupId;
    const quiz = this.parent.quiz;
    if (groupId && quiz?.questions) {
      const questionIds = quiz.questions.slice(
        this.selectedQuestions().length ?? 0,
        (this.selectedQuestions().length ?? 0) + (environment.production ? 10 : 2),
      );
      if (questionIds && questionIds.length > 0) {
        this.quizService
          .getQuizQuestions(groupId, questionIds)
          .then((qList) => {
            this.selectedQuestions.set([
              ...this.selectedQuestions(),
              ...(qList as QuizQuestion[]),
            ]);
          })
          .catch((error) => {
            this.logger.error('Error loading quiz questions: ', error);
            this.utilsService.onShowAlert(
              {
                code: 'LOAD_QUESTIONS_ERROR',
                title: 'Error Loading Questions',
                message: 'Failed to load quiz questions. Please try again later.',
              } as AppExceptionModel,
              false,
            );
          });
      }
    }
  }

  loadAttempts() {
    const groupId = this.parent.groupId;
    const quizId = this.parent.quizId;
    const user = this.parent.user;

    if (!groupId || !quizId || !user?.uid) {
      return;
    }

    this.loadingAttempts.set(true);

    this.parent.group$
      .pipe(
        take(1),
        switchMap((group) => {
          if (group?.memberStatus !== MembershipStatus.active) {
            this.loadingAttempts.set(false);
            return EMPTY;
          }

          try {
            return this.quizService.getQuizAttempts({
              groupId,
              quizId,
              userId: user.uid!,
              memberType: group?.memberType ?? [],
              limitRecords: 10,
            });
          } catch (err: any) {
            this.logger.error(
              'Sync error in getQuizAttempts call',
              JSON.stringify(err),
            );
            return throwError(() => err);
          }
        }),
      )
      .subscribe({
        next: (attempts) => {
          this.logger.debug(
            'Quiz attempts loaded successfully: ',
            attempts.length,
          );
          this.attempts$ = of(attempts);
          this.hasExistingAttempt.set(attempts.length > 0);
          this.parent.hasExistingAttempt.set(attempts.length > 0);
          this.loadingAttempts.set(false);

          const completedAttempt = attempts?.find((a) => a.completed);
          const quiz = this.parent.quiz;
          if (completedAttempt && quiz?.quizStatus?.toString() === QuizStatus.completed) {
            this.isLoadingParticipantData.set(true);
            this.quizService
              .getUserAnswers(
                this.parent.groupId!,
                this.parent.quizId!,
                completedAttempt.id,
                this.parent.user?.uid!
              )
              .then((answers) => {
                this.participantAnswers.set(answers);
                this.calculateParticipantScore(completedAttempt, answers);
                this.isLoadingParticipantData.set(false);
              })
              .catch((err) => {
                this.logger.error('Error loading participant answers:', err);
                this.isLoadingParticipantData.set(false);
              });
          }
        },
        error: (error) => {
          this.logger.error(
            `Error loading quiz attempts: ${JSON.stringify(error, null, 2)}`,
          );

          this.utilsService.onShowAlert(
            {
              code: 'LOAD_ATTEMPTS_ERROR',
              title: 'Error Loading Attempts',
              message: `Failed to load your quiz attempts. Please try again later.`,
            } as AppExceptionModel,
            false,
          );
          this.loadingAttempts.set(false);
        },
      });
  }

  calculateParticipantScore(attempt: any, answers: any[]) {
    const totalQuestions = this.parent.quiz?.questions?.length || answers.length || 0;
    const correctCount = answers.filter((a) => a.isCorrect).length;
    const skippedCount = answers.filter((a) => a.userAnswer === 'Skipped' || a.userAnswer === 'Time Expired').length;
    const wrongCount = answers.length - correctCount - skippedCount;
    const unansweredCount = totalQuestions - answers.length + skippedCount;

    this.participantScore.set({
      score: attempt.userScore ?? 0,
      totalQuestions,
      correctCount,
      wrongCount,
      unansweredCount,
      percentage: totalQuestions > 0 ? Math.round(((attempt.userScore ?? 0) / (this.parent.quiz?.totalScore || 1)) * 100) : 0
    });
  }

  getCorrectAnswer(item: any): string {
    if (item.correctOptionId) {
      const option = item.options?.find(
        (opt: any) => opt.id === item.correctOptionId,
      );
      return option ? option.text : 'N/A';
    }
    return item.simpleAnswer || 'N/A';
  }

  isStatusChangeable(roles: any): boolean {
    const quiz = this.parent.quiz;
    if (!quiz) return false;

    const isAuthorized =
      roles?.management ||
      (roles?.faculty && quiz.createdById === this.parent.user?.uid);

    if (!isAuthorized) return false;

    return [
      QuizStatus.scheduled,
      QuizStatus.waiting,
      QuizStatus.inProgress,
    ].includes(quiz.quizStatus);
  }

  toggleStatusDropdown(event: Event, roles: any) {
    if (!this.isStatusChangeable(roles)) return;
    event.stopPropagation();
    this.showStatusDropdown.update((show) => !show);
  }

  onCompleteQuiz(event: Event) {
    event.stopPropagation();
    this.showStatusDropdown.set(false);

    const quiz = this.parent.quiz;
    if (!quiz) return;

    this.utilsService
      .onRequestConfirmation({
        title: 'Close Quiz',
        description: `Are you sure you want to close the quiz "${quiz.title}" and compile its analytics? This action cannot be undone.`,
        action: 'Close',
        showType: AlertType.warning,
        buttonColor: 'bg-emerald-500',
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.quizService
          .updateQuiz(quiz.id, {
            ...quiz,
            quizStatus: QuizStatus.completed,
          } as Quiz)
          .then(() => {
            this.logger.debug('Quiz completed successfully');
            this.toastrService.success(
              `Quiz "${quiz.title}" closed successfully`,
            );
            this.parent.loadQuizDetails();
          })
          .catch((error) => {
            this.logger.error('Error completing quiz:', error);
            this.toastrService.error('Failed to close quiz');
          });
      });
  }

  onCancelQuiz(event: Event) {
    event.stopPropagation();
    this.showStatusDropdown.set(false);

    const quiz = this.parent.quiz;
    if (!quiz) return;

    this.utilsService
      .onRequestConfirmation({
        title: 'Cancel Quiz',
        description: `Are you sure you want to cancel the quiz "${quiz.title}"? This action cannot be undone.`,
        action: 'Cancel',
        showType: AlertType.warning,
        buttonColor: 'bg-red-500',
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.quizService
          .updateQuiz(quiz.id, {
            ...quiz,
            quizStatus: QuizStatus.cancelled,
          } as Quiz)
          .then(() => {
            this.logger.debug('Quiz cancelled successfully');
            this.toastrService.success(
              `Quiz "${quiz.title}" cancelled successfully`,
            );
            this.parent.loadQuizDetails();
          })
          .catch((error) => {
            this.logger.error('Error cancelling quiz:', error);
            this.toastrService.error('Failed to cancel quiz');
          });
      });
  }

  @HostListener('document:click')
  closeDropdown() {
    this.showStatusDropdown.set(false);
  }
}
